/**
 * D57: a failure shows a screen, never a blank page.
 *
 * React unmounts the whole tree on an uncaught render error, so without this
 * the result is a white page with the reason only in the console. That is the
 * worst outcome available: against *slower or less reliable than pen and paper
 * at 3am* it offers the operator nothing at all to do.
 *
 * Boundaries catch render and lifecycle errors only. Event-handler throws and
 * unhandled promise rejections do not reach here — `failures.ts` and the
 * global listeners in `App.tsx` cover those.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { describeError, type Failure } from './failures.js';

interface Props {
  readonly children: ReactNode;
  /** What broke, for the message. "this screen" when a route boundary. */
  readonly what?: string;
}

interface State {
  readonly failure: Failure | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { failure: null };

  static getDerivedStateFromError(error: unknown): State {
    return { failure: describeError(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The console is not the audience — the screen is — but a developer with
    // the console open should still get the component stack.
    console.error('Caught by an error boundary:', error, info.componentStack);
  }

  private readonly retry = (): void => {
    this.setState({ failure: null });
  };

  override render(): ReactNode {
    const { failure } = this.state;
    if (failure === null) return this.props.children;

    return (
      <FailureScreen failure={failure} what={this.props.what ?? 'This screen'} onRetry={this.retry} />
    );
  }
}

/**
 * Deliberately plain. No date formatting, no domain calls, no network.
 *
 * The crash that prompted D57 was a formatter throwing during render, and a
 * fallback that formatted a timestamp would fail in precisely the circumstance
 * it exists for.
 */
function FailureScreen({
  failure,
  what,
  onRetry,
}: {
  readonly failure: Failure;
  readonly what: string;
  readonly onRetry: () => void;
}): React.JSX.Element {
  return (
    <section role="alert">
      <h2>{what} stopped working</h2>
      <p>Nothing has been lost. Try again — this usually clears it.</p>

      <p>
        <button type="button" onClick={onRetry}>
          Try again
        </button>
      </p>

      <details>
        <summary>Show details</summary>
        <pre>{failure.detail}</pre>
      </details>

      {/*
        Reload is offered second, and said to be a last resort, on purpose.
        There is no service worker yet, so the app shell does not load offline:
        reloading on a checkpoint phone that has lost signal turns a broken
        screen into a dead one. This ordering reverses when the service worker
        lands — see the Stage A carried items.
      */}
      <p>
        If trying again does not help, reload the page — but only as a last resort on a checkpoint
        phone, because the app cannot reload without a connection.
      </p>
      <p>
        <button
          type="button"
          onClick={() => {
            window.location.reload();
          }}
        >
          Reload
        </button>
      </p>
    </section>
  );
}
